import type { FC } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type ApiLoyaltyCard from 'src/core/models/api/ApiLoyaltyCard';
import type ApiLoyaltyWorkspace from 'src/core/models/api/ApiLoyaltyWorkspace';
import Colors from 'src/core/utils/Colors';
import Constants from 'src/core/utils/Constants';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Translations from 'src/core/utils/Translations';
import { useLeaveAppStore } from 'src/domain/stores/useLeaveAppStore';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import { PrimaryButton, SecondaryButton } from 'src/presentation/components/molecules/Button';
import ThermenImage from 'src/presentation/components/molecules/ThermenImage';
import { HtmlView } from 'src/presentation/components/organisms/HtmlView';
import type { LoyaltyNavigation } from 'src/presentation/navigation/types/NavigationTypes';

interface Props {
	card: ApiLoyaltyCard;
	onNavigate: () => void;
}

const BenefitResortPicker: FC<Props> = ({ card, onNavigate }: Props) => {
	const navigation = useNavigation<StackNavigationProp<LoyaltyNavigation>>();
	const { captureEvent } = useServicesStore();
	const { confirmLeaving } = useLeaveAppStore();

	const workspaces = card.workspaces ?? [];

	const redeem = (workspace: ApiLoyaltyWorkspace) => {
		const url = workspace.reservation_flow_url;

		if (!url) {
			captureEvent({ message: 'Redeeming a loyalty card without a reservation flow url', extra: { code: card.code } });

			return;
		}

		confirmLeaving({
			url,
			description: Translations.reserveLeaveAppDescription,
			webMessage: Constants.bridgeMessageReservationFlowOpen,
		});
	};

	// The resort screen sits behind the sheet, so the sheet steps aside first.
	const openResort = (workspaceId: number) => {
		onNavigate();
		navigation.navigate(Constants.screenLoyaltyResort, { workspaceId });
	};

	return (
		<>
			<View style={styles.heading}>
				<Text style={styles.title}>{card.name}</Text>
				<Text style={styles.subtitle}>{Translations.loyaltySelectResort}</Text>
			</View>

			{workspaces.map((workspace) => (
				<View key={workspace.id} style={styles.card}>
					<View style={styles.resortRow}>
						<ThermenImage source={{ uri: workspace.logo }} style={styles.logo} />
						<Text style={styles.resort}>{workspace.title}</Text>
					</View>
					<View style={styles.details}>
						<Text style={styles.description}>{workspace.description}</Text>
						<HtmlView html={workspace.price_description ?? ''} />
					</View>
					<View style={styles.actions}>
						<SecondaryButton
							backgroundColor={Colors.white}
							extraStyle={styles.action}
							label={Translations.resortInfo}
							onPress={() => openResort(workspace.id)}
						/>
						<PrimaryButton extraStyle={styles.action} label={Translations.loyaltyRedeem} onPress={() => redeem(workspace)} />
					</View>
				</View>
			))}
		</>
	);
};

const styles = StyleSheet.create({
	heading: {
		gap: Dimens.gap4,
	},
	title: {
		fontFamily: Fonts.ptSerif,
		fontSize: Dimens.fontSize22,
		lineHeight: Dimens.lineHeight30,
		color: Colors.gray900,
	},
	subtitle: {
		fontFamily: Fonts.barlow,
		fontSize: Dimens.fontSize14,
		lineHeight: Dimens.lineHeight21,
		color: Colors.gray550,
	},
	card: {
		backgroundColor: Colors.gray75,
		borderRadius: Dimens.borderRadius6,
		padding: Dimens.padding16,
		gap: Dimens.gap16,
	},
	resortRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: Dimens.gap16,
	},
	logo: {
		height: Dimens.size40,
		width: Dimens.size40,
	},
	resort: {
		flex: Dimens.flex1,
		fontFamily: Fonts.ptSerif,
		fontSize: Dimens.fontSize16,
		lineHeight: Dimens.lineHeight24,
		color: Colors.gray900,
	},
	details: {
		flexDirection: 'row',
		alignItems: 'flex-end',
		justifyContent: 'space-between',
		gap: Dimens.gap16,
	},
	description: {
		flex: Dimens.flex1,
		fontFamily: Fonts.barlow,
		fontSize: Dimens.fontSize14,
		lineHeight: Dimens.lineHeight18,
		color: Colors.gray550,
	},
	actions: {
		flexDirection: 'row',
		gap: Dimens.gap12,
	},
	action: {
		flex: Dimens.flex1,
	},
});

export default BenefitResortPicker;
